<template>
  <div>
    <event-header :event="event">
      <basic-button
        @click="update"
        v-if="hasTicketDesign"
        :loading="loading"
        :disabled="loading"
        color="white"
        leading-icon="save"
      >Speichern</basic-button>
    </event-header>

    <div
      v-if="!hasTicketDesign"
      class="mt-8 p-8 flex flex-col md:flex-row justify-between items-center md:items-stretch rounded-lg shadow-lg"
    >
      <img class="mb-10 md:mb-0 w-1/3" src="@/assets/images/undraw_design_sprint_x3eb.svg" />

      <div class="flex flex-col md:ml-12">
        <h4 class="text-xl leading-6 text-gray-900">Noch kein Ticket-Design angelegt</h4>
        <p class="mt-2 mb-6 text-base leading-6 text-gray-500">
          Einlasszeiten, Programmtitel, wichtige Informationen für die Veranstaltung?
          Textinformationen und Logos können mithilfe des Ticket-Designs umgesetzt werden.
        </p>

        <basic-button
          @click="create"
          class="mt-auto"
          leading-icon="rocket-launch"
          size="lg"
          :loading="loading"
          :disabled="loading"
        >Ticket-Design anlegen</basic-button>
      </div>
    </div>

    <div v-else>
      <div class="flex justify-center w-full">
        <div class="w-full md:w-2/3 rounded shadow-lg overflow-hidden">
          <interactive-ticket :design="mergedDesign" />
        </div>
      </div>

      <div class="mt-8 grid grid-cols-1 row-gap-4 col-gap-4 sm:grid-cols-12">
        <basic-input
          v-model="ticketDesign.header_1_1"
          :placeholder="mergedDesign.header_1_1"
          label="Zeile 1"
          class="sm:col-span-6"
          :error="errors['header_1_1']"
        />

        <basic-input
          v-model="ticketDesign.subheader_1_2"
          placeholder="z.B. Zusatztitel"
          label="Zeile 1b"
          class="sm:col-span-6"
          :error="errors['subheader_1_2']"
        />

        <basic-input
          v-model="ticketDesign.header_2_1"
          label="Zeile 2"
          :placeholder="mergedDesign.header_2_1"
          class="sm:col-span-6"
          :error="errors['header_2_1']"
        />

        <basic-input
          v-model="ticketDesign.header_3_1"
          label="Zeile 3"
          :placeholder="mergedDesign.header_3_1"
          class="sm:col-span-6"
          :error="errors['header_3_1']"
        />

        <basic-input
          v-model="ticketDesign.text_6_1"
          label="Allgemeine Informationen"
          placeholder="z.B. Hinweise zum Einlass"
          class="sm:col-span-8"
          hint="Zeile 1"
          :error="errors['text_6_1']"
        />

        <basic-input
          v-model="ticketDesign.text_6_2"
          class="sm:col-span-8"
          hint="Zeile 2"
          :error="errors['text_6_2']"
        />

        <basic-input
          v-model="ticketDesign.text_6_3"
          class="sm:col-span-8"
          hint="Zeile 3"
          :error="errors['text_6_3']"
        />

        <basic-image-cropper
          label="Logo"
          class="sm:col-span-4 sm:row-start-3 sm:col-start-9 sm:row-span-3"
          force-size="600,400"
          @input="setLogoInput"
          @url="setLogoUrl"
          :value="ticketDesign.logo"
        />
      </div>
    </div>
  </div>
</template>

<script>
import EventHeader from "@/components/EventHeader";
import BasicImageCropper from "@/components/basic/ImageCropper";
import InteractiveTicket from "@/components/InteractiveTicket";
import ticketDesignClient from "@/api/central/ticket-designs";

import _cloneDeep from "lodash/cloneDeep";
import _isNull from "lodash/isNull";
import _merge from "lodash/merge";
import _pickBy from "lodash/pickBy";
import _isObject from "lodash/isObject";

import dayjs from "dayjs";

export default {
  components: {
    EventHeader,
    InteractiveTicket,
    BasicImageCropper
  },

  computed: {
    event() {
      return this.$store.state.editor.event;
    },
    hasTicketDesign() {
      return !_isNull(this.ticketDesign);
    },
    mergedDesign() {
      let defaults = {
        header_1_1: this.event.title,
        header_2_1: this.event.venue.name,
        text_2_2: this.event.venue.street,
        text_2_3: `${this.event.venue.zip_code} ${this.event.venue.city}`,
        header_3_1: `${dayjs(this.event.start_date.local).format(
          "dddd, DD.MM.YYYY HH:mm"
        )} Uhr`,
        header_4_2: "Freie Platzwahl",
        header_5_1: "EUR 10,00 Standard",
        subtext_5_1: "inkl. Gebühren",
        header_7_1: dayjs(this.event.start_date.local).format("DDMMYY"),
        header_8_1: dayjs(this.event.start_date.local).format("DDMMYY")
      };

      let ticketDesign = _pickBy(this.ticketDesign, value => {
        return !_isNull(value) && (value.length > 0 || _isObject(value));
      });

      return _merge(defaults, ticketDesign);
    }
  },

  data() {
    return {
      ticketDesign: _cloneDeep(
        this.$store.state.editor.event.default_ticket_design
      ),
      loading: false,
      errors: {}
    };
  },

  methods: {
    create() {
      this.loading = true;

      ticketDesignClient
        .store(this.event.id)
        .then(({ data }) => {
          this.ticketDesign = data.data;

          this.$store.dispatch("editor/refresh");
        })
        .finally(() => (this.loading = false));
    },

    update() {
      this.loading = true;
      this.errors = {};

      ticketDesignClient
        .update(this.ticketDesign.id, this.ticketDesign)
        .then(() => {
          this.$store.dispatch("editor/refresh");
          this.$toasted.global.saved();
        })
        .catch(({ response }) => {
          this.errors = response.data.errors;
          this.$toasted.global.error();
        })
        .then(() => (this.loading = false));
    },

    setLogoUrl(url) {
      _isNull(this.ticketDesign.logo) &&
        this.$set((this.ticketDesign.logo = {}));

      this.$set(this.ticketDesign.logo, "url", url);
    },

    setLogoInput(input) {
      _isNull(input) && this.$set(this.ticketDesign.logo, "url", "");

      this.ticketDesign.logo_id = input;
    }
  }
};
</script>